import React, { useState } from 'react';
import useAdminReviewList from './useAdminReviewList';
import { getPendingBusinessVerifications, approveBusinessVerification, rejectBusinessVerification } from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import {
  CheckCircle,
  XCircle,
  Building2,
  Mail,
  Phone,
  Globe,
  Search,
  X,
  IdCard,
  Camera,
  ExternalLink
} from 'lucide-react';
import AdminPageHeader from './AdminPageHeader';
import ProtectedDocument from '../common/ProtectedDocument';
import './AdminStyles.scss';

// --- Helpers ---


const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }
  }
};

const MetricCard = ({ icon: Icon, value, label, iconClass = "dashboard-view__metric-icon-wrap--neutral" }) => (
  <motion.div className="dashboard-view__metric-card" variants={itemVariants}>
    <div className="dashboard-view__metric-label">{label}</div>
    <div className="dashboard-view__metric-row">
        <span className="dashboard-view__metric-value">{value}</span>
    </div>
    <div className="dashboard-view__metric-content" style={{ marginTop: '12px' }}>
        <div className={`dashboard-view__metric-icon-wrap ${iconClass}`}>
            <Icon className="dashboard-view__metric-icon" />
        </div>
    </div>
  </motion.div>
);

const DocumentPreview = ({ document, title, icon }) => (
  <div className="document-preview-lux">
    <div className="doc-header">{icon}<span>{title}</span></div>
    <ProtectedDocument url={document} title={title} />
  </div>
);

const BusinessVerifications = () => {
  const [error, setError] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const { currentUser } = useAuth();
  const canReview = Boolean(currentUser?.is_superuser);

  const { results: pendingVerifications, loading, error: loadError, page, setPage, pagination, summary, countries, refresh } = useAdminReviewList(getPendingBusinessVerifications, { search: searchTerm, country: filterCountry });
  const filteredVerifications = pendingVerifications;

  const handleApprove = async (businessId) => {
    setProcessing(true);
    try {
      await approveBusinessVerification(businessId);
      refresh();
      setSuccess(`Aprobado con éxito.`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al aprobar');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await rejectBusinessVerification(selectedBusiness.id, rejectionReason);
      refresh();
      setShowRejectModal(false);
      setSuccess('Rechazado correctamente.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al rechazar');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <div className="dashboard-loading"><div className="spinner" /></div>;

  return (
    <motion.div className="dashboard-view__inner" variants={containerVariants} initial="hidden" animate="visible">
        <AdminPageHeader 
          title="Verificaciones de Negocios" 
          subtitle="Audita y aprueba el acceso de marcas al ecosistema FOTH."
          breadcrumbs="Negocios" 
        />

        <div className="dashboard-view__grid">
            <div className="dashboard-view__metric-grid">
               <MetricCard icon={Building2} label="PENDIENTES" value={summary.total || 0} iconClass="dashboard-view__metric-icon-wrap--neutral" />
               <MetricCard icon={Globe} label="PAÍSES" value={summary.countries || 0} iconClass="dashboard-view__metric-icon-wrap--gold" />
            </div>
        </div>

        <div className="dashboard-view__panel">
            <div className="luxury-filters">
                <div className="search-box-modern">
                    <Search size={18} />
                    <input 
                        type="text" 
                        placeholder="Buscar marca o email..." 
                        className="admin-input-minimal"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="filter-actions">
                    <select className="admin-select-minimal" value={filterCountry} onChange={e => setFilterCountry(e.target.value)}>
                        <option value="">Todos los países</option>
                        {countries.map(({ id, name }) => <option key={id} value={id}>{name}</option>)}
                    </select>
                </div>
            </div>

            <AnimatePresence>
                {success && <motion.div className="alert-luxury alert-luxury--success" role="status" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>{success}</motion.div>}
                {(error || loadError) && <motion.div className="alert-luxury alert-luxury--danger" role="alert" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>{error || loadError}</motion.div>}
            </AnimatePresence>

            <div className="verifications-stack">
                {filteredVerifications.length === 0 ? (
                    <div className="empty-state-luxury">
                        <Building2 size={40} opacity={0.2} />
                        <h3>Sin solicitudes pendientes</h3>
                    </div>
                ) : (
                    filteredVerifications.map(biz => (
                        <motion.div key={biz.id} className="verification-item-lux" variants={itemVariants}>
                            <div className="item-header">
                                <div className="biz-info">
                                    <div className="avatar-box"><Building2 size={20}/></div>
                                    <div className="text-box">
                                        <h4>{biz.company_name}</h4>
                                        <span>Registrado: {new Date(biz.user.date_join || Date.now()).toLocaleDateString()}</span>
                                    </div>
                                </div>
                                <div className="location-tag">
                                    <Globe size={14} /> {biz.user.country_name || 'País no especificado'}
                                </div>
                            </div>

                             <div className="details-grid">
                                <div className="detail-col">
                                    <label>Contacto</label>
                                    <p><Mail size={12}/> {biz.user.email}</p>
                                    <p><Phone size={12}/> {biz.user.phone_number || 'N/A'}</p>
                                </div>
                                <div className="detail-col">
                                    <label>Identificación</label>
                                    <p><IdCard size={12}/> RUC: {biz.ruc_tax_id}</p>
                                </div>
                                <div className="detail-col">
                                    <label>Instagram</label>
                                    {biz.instagram_account_connected ? (
                                        <div className="ig-admin-status">
                                            <a 
                                                href={`https://www.instagram.com/${biz.instagram_account}/`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="ig-handle-link"
                                            >
                                                <Camera size={12} color="#E1306C" /> 
                                                <strong>@{biz.instagram_account}</strong>
                                                <ExternalLink size={10} style={{ opacity: 0.5 }} />
                                            </a>
                                            <p className="ig-followers">
                                                {biz.instagram_followers?.toLocaleString() || 0} seguidores
                                            </p>
                                        </div>
                                    ) : (
                                        <p className="status-badge-soft-danger">No conectado</p>
                                    )}
                                </div>
                            </div>

                            <div className="docs-panel">
                                <label className="sec-label">Documentación Adjunta</label>
                                <div className="docs-grid-lux">
                                    <DocumentPreview icon={<IdCard size={16}/>} title="ID Representante" document={biz.id_document_url} />
                                    <DocumentPreview title="Documento empresarial" document={biz.business_document_url} />
                                </div>
                            </div>

                            <div className="action-row">
                                {canReview && <button className="admin-btn admin-btn--primary" onClick={() => handleApprove(biz.id)} disabled={processing}>
                                    <CheckCircle size={16} /> Aprobar
                                </button>}
                                {canReview && <button className="admin-btn admin-btn--outline" style={{ color: '#ff4d5e', borderColor: 'rgba(255,77,94,0.2)' }} onClick={() => { setSelectedBusiness(biz); setShowRejectModal(true); }}>
                                    <XCircle size={16} /> Rechazar
                                </button>}
                            </div>
                        </motion.div>
                    ))
                )}
            </div>
            <div className="admin-pagination"><span>{pagination.count} solicitudes</span><div><button type="button" className="admin-btn admin-btn--outline admin-btn--sm" disabled={page <= 1} onClick={() => setPage(value => value - 1)}>Anterior</button><span>Página {page} de {pagination.pages || 1}</span><button type="button" className="admin-btn admin-btn--outline admin-btn--sm" disabled={page >= (pagination.pages || 1)} onClick={() => setPage(value => value + 1)}>Siguiente</button></div></div>
        </div>

        {/* Reject Modal */}
        <AnimatePresence>
            {showRejectModal && (
                <div className="modal-luxury-overlay" onClick={() => setShowRejectModal(false)}>
                    <motion.div role="dialog" aria-modal="true" aria-labelledby="business-reject-title"
                        className="modal-luxury-content" 
                        initial={{ scale: 0.9, opacity: 0 }} 
                        animate={{ scale: 1, opacity: 1 }} 
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-luxury-header">
                            <h3 id="business-reject-title">Rechazar Solicitud</h3>
                            <button type="button" aria-label="Cerrar" className="close-btn-circle" onClick={() => setShowRejectModal(false)}><X size={18}/></button>
                        </div>
                        <form onSubmit={handleReject}>
                            <div className="modal-luxury-body">
                                <p>Por favor indica el motivo del rechazo para <strong>{selectedBusiness?.company_name}</strong>. Este mensaje se enviará al usuario.</p>
                                <textarea 
                                    className="admin-textarea" 
                                    style={{ width: '100%', minHeight: '100px', borderRadius: '12px', padding: '12px', border: '1px solid #d8dee8', marginTop: '12px' }}
                                    placeholder="Ej: Documentos ilegibles o incompletos..."
                                    value={rejectionReason}
                                    onChange={e => setRejectionReason(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="modal-luxury-footer">
                                <button className="admin-btn admin-btn--outline" type="button" onClick={() => setShowRejectModal(false)}>Cancelar</button>
                                <button className="admin-btn" style={{ background: '#ff4d5e' }} type="submit" disabled={processing}>Confirmar Rechazo</button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    </motion.div>
  );
};

export default BusinessVerifications;
